
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { TenantProvider } from "@/contexts/TenantContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RootRedirect } from "@/components/auth/RootRedirect";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { useGlobalErrorHandler } from "@/hooks/useGlobalErrorHandler";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ThemeProvider } from "next-themes";

// Import pages
import QuickscanPage from "./pages/QuickscanPage";
import { CRMLayout } from "./components/layout/CRMLayout";
import CRMDashboard from "./pages/CRMDashboard";
import CRMOrganisaties from "./pages/CRMOrganisaties";
import CRMContacts from "./pages/CRMContacts";
import CRMLeads from "./pages/CRMLeads";
import CRMProjects from "./pages/CRMProjects";
import CRMProjectTemplates from "./pages/CRMProjectTemplates";
import CRMQuotes from "./pages/CRMQuotes";
import CRMMailTriage from "./pages/CRMMailTriage";
import CRMQuoteDetail from "./pages/CRMQuoteDetail";
import CRMQuoteTemplates from "./pages/CRMQuoteTemplates";
import CRMQuoteTemplateEdit from "./pages/CRMQuoteTemplateEdit";
import CRMQuoteTemplateDesign from "./pages/CRMQuoteTemplateDesign";
import CRMSites from "./pages/CRMSites";
import CRMTodos from "./pages/CRMTodos";
import CRMTickets from "./pages/CRMTickets";
import CRMUsers from "./pages/CRMUsers";
import CRMPortalUsers from "./pages/CRMPortalUsers";
import CRMUserManagement from "./pages/CRMUserManagement";
import CRMSettings from "./pages/CRMSettings";
import CRMErrorReports from "./pages/CRMErrorReports";
import CRMMailAi from "./pages/CRMMailAi";
import CRMBatteryPlanner from "./pages/CRMBatteryPlanner";
import CRMNotifications from "./pages/CRMNotifications";
import CRMCalendar from "./pages/CRMCalendar";
import CRMBusinesscase from './pages/CRMBusinesscase';
import CRMBusinesscases from './pages/CRMBusinesscases';
import CRMProjectBusinesscase from './pages/CRMProjectBusinesscase';
import CRMFlexE from './pages/CRMFlexE';
import CRMFlexENew from './pages/CRMFlexENew';
import CRMFlexEDetail from './pages/CRMFlexEDetail';
import CRMFlexEMachines from './pages/CRMFlexEMachines';
import CRMFlexEScenarios from './pages/CRMFlexEScenarios';
import CRMAnalyses from './pages/CRMAnalyses';
import CRMAnalysesHub from './pages/CRMAnalysesHub';
import CRMAnalysesHubV2 from './pages/CRMAnalysesHubV2';
import CRMPiekvermogen from './pages/CRMPiekvermogen';
import CRMPiekanalyseDetail from './pages/CRMPiekanalyseDetail';
import CRMTranscripties from './pages/CRMTranscripties';
import CRMEPSEOverview from './pages/CRMEPSEOverview';
import CRMEPSESiteDetail from './pages/CRMEPSESiteDetail';
import CRMEPSEReport from './pages/CRMEPSEReport';
import CRMContactDetail from './pages/CRMContactDetail';
import CRMProjectDetail from './pages/CRMProjectDetail';
import CRMOrganisatieDetail from './pages/CRMOrganisatieDetail';
import CRMDocumentation from './pages/CRMDocumentation';
import CRMSandbox from './pages/CRMSandbox';
import CRMSandboxEnergyFlow from './pages/CRMSandboxEnergyFlow';
import CRMWebsite from './pages/CRMWebsite';
import CRMWebsiteTasks from './pages/CRMWebsiteTasks';
import CRMWebsiteTaskDetail from './pages/CRMWebsiteTaskDetail';
import CRMWebsiteContent from './pages/CRMWebsiteContent';
import CRMWebsiteContentDetail from './pages/CRMWebsiteContentDetail';
import CRMWebsiteNotes from './pages/CRMWebsiteNotes';
import CRMWebsiteIdeas from './pages/CRMWebsiteIdeas';
import { CRMWebsiteSite, CRMWebsiteSiteLijst, CRMWebsiteSiteBewerk } from './pages/CRMWebsiteSite';
import CRMWebsiteOverzicht from './pages/CRMWebsiteOverzicht';
import CRMArchief from './pages/CRMArchief';
import CRMTekenaar from './pages/CRMTekenaar';
import CRMTekenaarEditor from './pages/CRMTekenaarEditor';
import { LaadpleinDesignerRoutes } from '@/features/laadplein-designer/routes';
import { EmsRoutes } from '@/features/ems/routes';
import { SiteRoutes } from '@/features/sites/routes';
import BriefingViewer from './pages/BriefingViewer';

import CRMIntakes from './pages/CRMIntakes';
import CRMIntakeDetail from './pages/CRMIntakeDetail';
import LaadpleinIntelligence from './pages/LaadpleinIntelligence';
import LaadpleinLocatieDetail from './pages/LaadpleinLocatieDetail';
import CRMSubsidies from './pages/CRMSubsidies';
import IntelligencePage from './pages/crm/IntelligencePage';

import AuthPage from "./pages/AuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminPage from "./pages/AdminPage";
import NotFound from "./pages/NotFound";

import ExternalIntakeSuccess from './pages/ExternalIntakeSuccess';
import ExternalIntakeWizard from './pages/ExternalIntakeWizard';
import ExternalDocumentUpload from './pages/ExternalDocumentUpload';

// Inspector Portal
import InspectorAuth from "./pages/InspectorAuth";
import InspectorDashboard from "./pages/InspectorDashboard";
import InspectorUpload from "./pages/InspectorUpload";
import InspectorHistory from "./pages/InspectorHistory";
import InspectorProjectDetail from "./pages/InspectorProjectDetail";
import InspectorInspectionDetail from "./pages/InspectorInspectionDetail";
import { InspectorLayout } from "./components/inspector/InspectorLayout";

// Client Portal
import ClientAuth from "./pages/ClientAuth";
import { ProtectedPortalRoute } from "./components/auth/ProtectedPortalRoute";

// Charging Portal
import { ChargingLayout } from "./components/layout/ChargingLayout";
import ChargingDashboard from "./pages/ChargingDashboard";
import ChargingOrganisaties from "./pages/ChargingOrganisaties";
import ChargingContacten from "./pages/ChargingContacten";
import ChargingAcquisitie from "./pages/ChargingAcquisitie";
import ChargingRealisatie from "./pages/ChargingRealisatie";
import ChargingOperatie from "./pages/ChargingOperatie";
import ChargingArchief from "./pages/ChargingArchief";
import ChargingTodos from "./pages/ChargingTodos";
import ChargingExport from "./pages/ChargingExport";
import ChargingConcurrentie from "./pages/ChargingConcurrentie";
import ChargingWensen from "./pages/ChargingWensen";
import ChargingLocationDetail from "./pages/ChargingLocationDetail";
import ChargingOpportunities from "./pages/ChargingOpportunities";
import ChargingOpportunityDetail from "./pages/ChargingOpportunityDetail";
import ChargingPresentatie from "./pages/ChargingPresentatie";
import ChargingGauwTabel from "./pages/ChargingGauwTabel";

// SharePoint
import SharePointCallback from "./pages/SharePointCallback";
import OAuthConsentPage from "./pages/OAuthConsentPage";

// Outlook OAuth
import OutlookCallback from "./pages/OutlookCallback";

const queryClient = new QueryClient();

// Capture PWA install prompt globally before any component mounts
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    (window as any).deferredPWAPrompt = e;
    window.dispatchEvent(new CustomEvent('pwa-prompt-available'));
  });
}

const AppContent = () => {
  useGlobalErrorHandler();
  
  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          {/* Smart root redirect based on auth status */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Auth pages */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/reset" element={<ResetPasswordPage />} />
          
          {/* SharePoint OAuth Callback */}
          <Route path="/sharepoint-callback" element={<SharePointCallback />} />

          {/* OAuth 2.1 consent for external AI clients (gv-crm-mcp) */}
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />
          
          {/* Outlook OAuth Callback */}
          <Route path="/crm/callback/outlook" element={
            <ProtectedRoute requiredRoles={['admin', 'super_admin', 'crm_user']}>
              <OutlookCallback />
            </ProtectedRoute>
          } />
          
          {/* Inspector Portal Auth */}
          <Route path="/inspector/auth" element={<InspectorAuth />} />
          
          {/* Inspector Portal Routes - Protected */}
          <Route path="/inspector" element={
            <ProtectedPortalRoute allowedRoles={['inspector']}>
              <InspectorLayout />
            </ProtectedPortalRoute>
          }>
            <Route index element={<InspectorDashboard />} />
            <Route path="upload" element={<InspectorUpload />} />
            <Route path="history" element={<InspectorHistory />} />
            <Route path="project/:projectId" element={<InspectorProjectDetail />} />
            <Route path="inspection/:inspectionId" element={<InspectorInspectionDetail />} />
          </Route>

          {/* Client Portal Auth */}
          <Route path="/client/auth" element={<ClientAuth />} />
          
          {/* Client Portal Routes - Protected */}
          <Route path="/client" element={
            <ProtectedPortalRoute allowedRoles={['client']}>
              <div className="min-h-screen flex items-center justify-center">
                <h1 className="text-2xl font-bold">Client Portal - Coming Soon</h1>
              </div>
            </ProtectedPortalRoute>
          } />

          {/* Charging Portal Routes - Protected (for dedicated charging users) */}
          <Route path="/charging" element={
            <ProtectedRoute requiredRoles={['charging_user']}>
              <ChargingLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ChargingDashboard />} />
            <Route path="acquisitie" element={<ChargingAcquisitie />} />
            <Route path="realisatie" element={<ChargingRealisatie />} />
            <Route path="operatie" element={<ChargingOperatie />} />
            <Route path="locatie/:locationId" element={<ChargingLocationDetail />} />
            <Route path="archief" element={<ChargingArchief />} />
            <Route path="todos" element={<ChargingTodos />} />
            <Route path="export" element={<ChargingExport />} />
            <Route path="concurrentie" element={<ChargingConcurrentie />} />
            <Route path="wensen" element={<ChargingWensen />} />
            <Route path="opportunities" element={<ChargingOpportunities />} />
            <Route path="opportunities/:opportunityId" element={<ChargingOpportunityDetail />} />
            <Route path="organisaties" element={<ChargingOrganisaties />} />
            <Route path="contacten" element={<ChargingContacten />} />
            <Route path="presentatie" element={<ChargingPresentatie />} />
            <Route path="gauw-tabel" element={<ChargingGauwTabel />} />
          </Route>
          
          {/* Quickscan */}
          <Route path="/quickscan/flex-e" element={<QuickscanPage />} />

          {/* External intake success (public) */}
          <Route path="/intake/success" element={<ExternalIntakeSuccess />} />

          {/* External intake wizard (public) */}
          <Route path="/intake/:token" element={<ExternalIntakeWizard />} />

          {/* External document upload (public) */}
          <Route path="/upload/:token" element={<ExternalDocumentUpload />} />
          
          {/* Admin page. De pagina deed zelf alleen een client-side check
              (AdminContext, wachtwoord in de broncode, vlag in localStorage).
              Nu ook achter de gewone rolcheck. Let op: dit is nog steeds
              client-side — de echte fix is een server-side rolcheck en die
              valt buiten F1. */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <AdminPage />
            </ProtectedRoute>
          } />
          
          {/* Top-level Dutch redirects */}
          <Route path="/contacten" element={<Navigate to="/crm/contacten" replace />} />
          <Route path="/projecten" element={<Navigate to="/crm/projecten" replace />} />
          <Route path="/gebruikers" element={<Navigate to="/crm/gebruikers" replace />} />
          <Route path="/sites" element={<Navigate to="/crm/sites" replace />} />
          <Route path="/instellingen" element={<Navigate to="/crm/instellingen" replace />} />
          
          {/* Briefing Viewer (outside CRM layout for clean fullscreen) */}
          <Route path="/crm/projects/:projectId/viewer/:briefingId" element={<BriefingViewer />} />

          {/* Full-screen Tekenaar Editor (outside CRM layout) */}
          <Route path="/crm/tekenaar/:projectId" element={
            <ProtectedRoute requiredRoles={['admin', 'super_admin', 'crm_user']}>
              <CRMTekenaarEditor />
            </ProtectedRoute>
          } />

          {/* Laadplein Designer V2 — geïsoleerde feature module */}
          {LaadpleinDesignerRoutes}

          {/* CRM Routes with Layout - STRICT role check */}
          <Route path="/crm" element={
            <ProtectedRoute requiredRoles={['admin', 'super_admin', 'crm_user']}>
              <CRMLayout />
            </ProtectedRoute>
          }>
            <Route index element={<CRMDashboard />} />
            <Route path="intelligence" element={<IntelligencePage />} />
            <Route path="klanten" element={<CRMOrganisaties />} />
            <Route path="organisaties" element={<CRMOrganisaties />} />
            <Route path="contacts" element={<CRMContacts />} />
            <Route path="contacten" element={<CRMContacts />} />
            <Route path="leads" element={<CRMLeads />} />
            <Route path="projects" element={<CRMProjects />} />
            <Route path="projecten" element={<CRMProjects />} />
            <Route path="project-templates" element={<CRMProjectTemplates />} />
            <Route path="sites" element={<CRMSites />} />
            {/* F9.12: de sitepagina met het EMS erin, binnen de CRM-layout. Locaties is
                de enige ingang; /crm/ems en /crm/ems/:locatieId verwijzen daarheen door.
                Intern voor het team; een klant komt hier niet (ADR 0010). */}
            {SiteRoutes}
            {EmsRoutes}
            <Route path="todos" element={<CRMTodos />} />
            <Route path="intakes" element={<CRMIntakes />} />
            <Route path="intake/:id" element={<CRMIntakeDetail />} />
            <Route path="tickets" element={<CRMTickets />} />
            <Route path="notifications" element={<CRMNotifications />} />
            <Route path="agenda" element={<CRMCalendar />} />
            <Route path="calendar" element={<CRMCalendar />} />
            <Route path="battery-planner" element={<CRMBatteryPlanner />} />
            <Route path="batterijen" element={<CRMBatteryPlanner />} />
            <Route path="businesscases" element={<CRMBusinesscases />} />
            <Route path="businesscases/:businesscaseId" element={<CRMBusinesscase />} />
            <Route path="tekenaar" element={<CRMTekenaar />} />
            <Route path="projects/:projectId/businesscase" element={<CRMProjectBusinesscase />} />
            {/* Let op: `flexe/:simId` staat vóór `flexe/machines` maar shadowt
                hem niet. React Router 6 rangschikt routes op specificiteit, niet
                op declaratievolgorde — een statisch segment scoort 10 punten,
                een dynamisch segment 3 (computeScore in @remix-run/router). De
                statische routes winnen dus altijd. Niet "fixen" door te
                herordenen; dat verandert niets. */}
            <Route path="flexe" element={<CRMFlexE />} />
            <Route path="flexe/new" element={<CRMFlexENew />} />
            <Route path="flexe/:simId" element={<CRMFlexEDetail />} />
            <Route path="flexe/machines" element={<CRMFlexEMachines />} />
            <Route path="flexe/scenarios" element={<CRMFlexEScenarios />} />
            
            {/* Analyses Routes */}
            <Route path="analyses" element={<CRMAnalyses />} />
            <Route path="analyses/piekvermogen" element={<CRMPiekvermogen />} />
            <Route path="analyses/piekvermogen/:analyseId" element={<CRMPiekanalyseDetail />} />
            <Route path="analyses/transcripties" element={<CRMTranscripties />} />
            <Route path="analyses/epse" element={<CRMEPSEOverview />} />
            <Route path="analyses/epse/:siteId" element={<CRMEPSESiteDetail />} />
            <Route path="analyses/epse/:siteId/report" element={<CRMEPSEReport />} />
            <Route path="analyses/hub" element={<CRMAnalysesHub />} />
            <Route path="analyses/hub-v2" element={<CRMAnalysesHubV2 />} />

            {/* Laadplein Intelligence */}
            <Route path="laadplein" element={<LaadpleinIntelligence />} />
            <Route path="laadplein/locatie/:id" element={<LaadpleinLocatieDetail />} />

            {/* Subsidie Module */}
            <Route path="subsidies" element={<CRMSubsidies />} />
            
            {/* Charging Routes in CRM */}
            <Route path="charging" element={<ChargingDashboard />} />
            <Route path="charging/acquisitie" element={<ChargingAcquisitie />} />
            <Route path="charging/realisatie" element={<ChargingRealisatie />} />
            <Route path="charging/operatie" element={<ChargingOperatie />} />
            <Route path="charging/archief" element={<ChargingArchief />} />
            <Route path="charging/todos" element={<ChargingTodos />} />
            <Route path="charging/export" element={<ChargingExport />} />
            <Route path="charging/concurrentie" element={<ChargingConcurrentie />} />
            <Route path="charging/wensen" element={<ChargingWensen />} />
            <Route path="charging/opportunities" element={<ChargingOpportunities />} />
            <Route path="charging/opportunities/:opportunityId" element={<ChargingOpportunityDetail />} />
            <Route path="charging/presentatie" element={<ChargingPresentatie />} />
            <Route path="charging/gauw-tabel" element={<ChargingGauwTabel />} />
            <Route path="charging/locatie/:locationId" element={<ChargingLocationDetail />} />
            
            <Route path="contacts/:contactId" element={<CRMContactDetail />} />
            <Route path="contacten/:contactId" element={<CRMContactDetail />} />
            <Route path="projects/:projectId" element={<CRMProjectDetail />} />
            <Route path="projecten/:projectId" element={<CRMProjectDetail />} />
            <Route path="organisaties/:orgId" element={<CRMOrganisatieDetail />} />
            <Route path="users" element={<CRMUsers />} />
            <Route path="gebruikers" element={<CRMUsers />} />
            <Route path="portal-users" element={<CRMPortalUsers />} />
            <Route path="user-management" element={<CRMUserManagement />} />
            <Route path="gebruikersbeheer" element={<CRMUserManagement />} />
            <Route path="error-reports" element={<CRMErrorReports />} />
            <Route path="mail-en-ai" element={<CRMMailAi />} />
            <Route path="documentatie" element={<CRMDocumentation />} />
            <Route path="sandbox" element={<CRMSandbox />} />
            <Route path="sandbox/energy-flow" element={<CRMSandboxEnergyFlow />} />
            
            {/* Website: één scherm met bezoek, formulieren, inhoud en signalen (ADR 0034); de planning eronder */}
            <Route path="website" element={<CRMWebsiteOverzicht />} />
            <Route path="website/planning" element={<CRMWebsite />} />
            <Route path="website/taken" element={<CRMWebsiteTasks />} />
            <Route path="website/taken/:taskId" element={<CRMWebsiteTaskDetail />} />
            <Route path="website/content" element={<CRMWebsiteContent />} />
            <Route path="website/content/:id" element={<CRMWebsiteContentDetail />} />
            <Route path="website/notities" element={<CRMWebsiteNotes />} />
            <Route path="website/ideeen" element={<CRMWebsiteIdeas />} />
            {/* Content van greenvision.energy zelf (schema website, ADR 0024) */}
            <Route path="website/site" element={<CRMWebsiteSite />} />
            <Route path="website/site/:soort" element={<CRMWebsiteSiteLijst />} />
            <Route path="website/site/:soort/:id" element={<CRMWebsiteSiteBewerk />} />
            <Route path="archief" element={<CRMArchief />} />
            <Route path="settings" element={<CRMSettings />} />
            <Route path="quotes" element={<CRMQuotes />} />
            {/* T3.5 - triage van binnengekomen post. */}
            <Route path="mail" element={<CRMMailTriage />} />
            <Route path="quotes/:quoteId" element={<CRMQuoteDetail />} />
            <Route path="quote-templates" element={<CRMQuoteTemplates />} />
            <Route path="quote-templates/:templateId" element={<CRMQuoteTemplateEdit />} />
            <Route path="quote-templates/:templateId/design" element={<CRMQuoteTemplateDesign />} />
            <Route path="instellingen" element={<CRMSettings />} />
            {/* Fallback for unknown subroutes */}
            <Route path="*" element={<Navigate to="/crm" replace />} />
          </Route>
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <TenantProvider>
          <NotificationProvider>
            <AppErrorBoundary>
              <AppContent />
            </AppErrorBoundary>
          </NotificationProvider>
        </TenantProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
